import React from "react";
import { useNavigate } from "react-router-dom";

// 1. Standard Page Imports
import PublicPage from "../pages/PublicPage";
import RegisterPage from "../auth/pre/SEC_02_02_RegisterController";
import SubscribePage from "../pages/SubscribePage";
import RegisteredDashboard from "../components/restricted/RegisteredDashboard";
import SignInPage from "../auth/pre/SEC_02_01_SignInController";
import VerifyEmailPage from "../auth/pages/VerifyEmailPage";
import ResetPasswordPage from "../auth/pages/ResetPasswordPage";

// 2. Security Guards
import { RequireAuth } from "../auth";
import PostSignInAccessGuard from "../auth/registered/SEC_03_01_PostSignInAccessGuard";
import RolePermissionGuard from "../auth/registered/SEC_03_02_RolePermissionGuard"; 

// 3. Post-Login Handoff
import SEC_03_00_PostLoginHandoff from "../auth/registered/SEC_03_00_PostLoginHandoff";

// --- 5. SEC_03 USER MANAGEMENT ---
import UserManagementMainView from "../registered/site-admin/SEC_03_Usermanagement/SEC_03_UserManagementMainView";

// 🟡 LEGACY COMPONENTS
import UserRoleAssignmentController from "../registered/site-admin/components/SEC_03_UserRoleAssignmentController";
import UserRoleAssignmentView from "../registered/site-admin/components/SEC_03_UserRoleAssignmentView";
import DataEntryWorkspace from "../components/restricted/data-entry/DataEntryWorkspace";

// --- SUSTINA & ADMIN HUB IMPORTS ---
import SiteAdminDashboard from "../registered/dashboard/SiteAdminDashboard";
import WorkflowRefreshESG from "../registered/site-admin/components/workflow_REFRESH_ESG_Taxonomy";
import SustinaTaxonomyView from "../registered/site-admin/components/SustinaTaxonomyView";
import BatchMonitorView from "../registered/upload/components/BatchMonitor/BatchMonitorView";
import WorkflowXls2SchemaController from "../registered/site-admin/components/Workflow-esg/Workflow_XLS_2_Schema_Controller";
import WorkflowHub from "../registered/site-admin/components/Workflow-esg/Workflow_Hub"; 

// 🛡️ Template Management
// Hidden 2026-10-03: legacy S3 Template Builder, superseded by the article-first flow.
// import TemplateManagementController from "../registered/site-admin/components/Workflow-esg/controllers/TemplateManagementController";
import XbrlProcessorController from "../registered/site-admin/components/Workflow-esg/controllers/XbrlProcessorController";

// 🚀 NEW: Report Definition Orchestrator (IMPORT THE VIEW, NOT THE CONTROLLER)
import DefinitionsOrchestrationView from "../registered/site-admin/report_definition/views/DefinitionsOrchestrationView";

// 🎯 AUTHORITY: Dedicated S3 Physical Inventory View
import S3MonitorView from "../registered/upload/components/S3Monitor/S3MonitorView";
import CorporateDetailsView from "../registered/corporate_details/CorporateDetailsView";
import CompanyDashboardView from "../registered/corporate_details/company_dashboard/CompanyDashboardView";
import AccountLedgerView from "../registered/corporate_details/account_ledger/AccountLedgerView";
import HrSyncView from "../registered/corporate_details/hr_sync/HrSyncView";
import CompanySsoPage from "../auth/sso/CompanySsoPage";
import SsoCallbackPage from "../auth/sso/SsoCallbackPage";
import CompanyEsgScopeView from "../registered/corporate_details/company_scope/CompanyEsgScopeView";
import DeclrAttributeDefinitionView from "../registered/site-admin/declr_attribute_definition/DeclrAttributeDefinitionView";
import DeclrAttributeGroupView from "../registered/corporate_details/declr_attribute_group/DeclrAttributeGroupView";
import DeclrLocalDeclarationView from "../registered/corporate_details/declr_local_declaration/DeclrLocalDeclarationView";
import DeclrAttributeMappingView from "../registered/site-admin/declr_attribute_mapping/DeclrAttributeMappingView";
import DeclrChartOfAccountsView from "../registered/corporate_details/company_chart_of_accounts/DeclrChartOfAccountsView";
import ReportComponentLibraryView from "../registered/site-admin/report_component_library/ReportComponentLibraryView";
// Dev/support only - hidden 2026-10-03 (model plumbing; the article screen will drive it).
// import InputObjectsView from "../registered/site-admin/input_objects/InputObjectsView";
import KnowledgeBaseView from "../registered/site-admin/knowledge_base/KnowledgeBaseView";
import KnowledgeBaseAdminPage from "../kb/KnowledgeBaseAdminPage";
import MeterCatalogueView from "../registered/site-admin/meter_catalogue/MeterCatalogueView";
import ReportManagementHub from "../registered/site-admin/report_management/ReportManagementHub";
import ReportingStepFrame from "../registered/site-admin/report_management/ReportingStepFrame";
import ConductReportingView from "../registered/site-admin/report_management/blueprint/ConductReportingView";
import SupplierWorkspaceView from "../registered/corporate_details/suppliers/SupplierWorkspaceView";
// 🎯 Aliases bypass the underscore naming rules for react/jsx-pascal-case
import RepresentativeController from "../registered/corporate_details/representative/SEC_RepresentativeController";
import RequestRolesPlaceholder from "../registered/request_roles/SEC_RequestRolesPlaceholder";

/**
 * 🛠️ CONFIGURATION
 */
const GLOBAL_ADMIN_ROLE = process.env.REACT_APP_GA || "GLOBALADMIN";
const SITE_ADMIN_ROLE = "SITEADMIN";

/**
 * 🔐 ROUTE WRAPPERS
 */

const RegisteredDashboardRoute = () => (
  <RequireAuth>
    <RegisteredDashboard />
  </RequireAuth>
);

const DataEntryWorkspaceRoute = () => (
  <RequireAuth>
    <DataEntryWorkspace />
  </RequireAuth>
);

const SiteAdminDashboardRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <SiteAdminDashboard />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const UserRoleAssignmentRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <UserRoleAssignmentController onSuccess={() => alert("Identity Database Updated Successfully")}>
        {(ctrl) => (
          <UserRoleAssignmentView
            onAssign={ctrl.assign}
            onHandshake={ctrl.handshake}
            isSubmitting={ctrl.isSubmitting}
            error={ctrl.error}
          />
        )}
      </UserRoleAssignmentController>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const SEC_03_ManagementPortalRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <RolePermissionGuard roles={[GLOBAL_ADMIN_ROLE, SITE_ADMIN_ROLE]}>
        <UserManagementMainView />
      </RolePermissionGuard>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const TaxonomyWorkflowRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <WorkflowRefreshESG />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const TaxonomyIngestRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <SustinaTaxonomyView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const SmartUploadMonitorRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <BatchMonitorView />
    </PostSignInAccessGuard> 
  </RequireAuth>
);

const S3InventoryMonitorRoute = () => {
  const targetBucket = process.env.REACT_APP_SITE_DIRTY_BUCKET;
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const config = { siteKey };

  return (
    <RequireAuth>
      <PostSignInAccessGuard>
        <S3MonitorView targetBucket={targetBucket} config={config} />
      </PostSignInAccessGuard> 
    </RequireAuth>
  );
};

const WorkflowXls2SchemaRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <WorkflowXls2SchemaController />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const WorkflowHubRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <RolePermissionGuard requiredRole={GLOBAL_ADMIN_ROLE}>
        <WorkflowHub />
      </RolePermissionGuard>
    </PostSignInAccessGuard>
  </RequireAuth>
);

/*
const TemplateBuilderRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <ReportingStepFrame stepKey="template-builder">
        <TemplateManagementController />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);
*/

const XbrlProcessorRoute = () => {
  const navigate = useNavigate();

  return (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <main id="main" className="page-main registered-page" data-scroll-container>
        <div className="page-main-inner">
          <section className="page-section">
            <div className="page-section__body d-flex flex-column align-items-center">
              <div style={{ width: "100%", maxWidth: "650px" }} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 text-secondary small mb-3"
                  onClick={() => navigate("/registered/site-admin")}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Return to Admin Dashboard
                </button>
                <div className="text-center">
                  <h1 className="h3 fw-bold mb-2">XBRL Processor</h1>
                  <p className="text-muted small mb-0">
                    Bring in a real, official XBRL taxonomy so its concepts are available
                    for use in Sustina. Declaring, using, and versioning a taxonomy through
                    a guided step-by-step process is coming next &mdash; for now this covers
                    the ingest step only.
                  </p>
                </div>
              </div>
              <XbrlProcessorController />
            </div>
          </section>
        </div>
      </main>
    </PostSignInAccessGuard>
  </RequireAuth>
  );
};

/**
 * 🎯 FIXED: ESG Report Definition Orchestrator Route
 * This now renders the VIEW component which properly hosts the logic hook.
 */
const ReportDefinitionOrchestrationRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <ReportingStepFrame stepKey="report-definition">
        <DefinitionsOrchestrationView />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const CorporateDetailsRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <CorporateDetailsView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const CompanyDashboardRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <CompanyDashboardView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

/*
const InputObjectsRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <ReportingStepFrame stepKey="input-objects">
        <InputObjectsView />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);
*/

const ReportComponentLibraryRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <ReportingStepFrame stepKey="component-library">
        <ReportComponentLibraryView />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);

// Shared @nrgpix/kb management screen — open to KBADMIN holders (the page and the
// procs both check the profile), so not tied to the global-admin role.
const KnowledgeBaseRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <KnowledgeBaseAdminPage />
    </PostSignInAccessGuard>
  </RequireAuth>
);

// The earlier bespoke screen, kept reachable until the shared one is signed off.
const KnowledgeBaseLegacyRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <KnowledgeBaseView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const MeterCatalogueRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <ReportingStepFrame stepKey="meter-catalogue">
        <MeterCatalogueView />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const ReportManagementRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <ReportManagementHub />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const RegisterCompanyReportingRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <ReportingStepFrame stepKey="register-company">
        <RepresentativeController />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);

// "Enter ESG Data" — standalone, reached from the Registered dashboard, not
// part of the Configure ESG Templates (report-management) flow.
const ConductReportingRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <ConductReportingView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const RepresentCompanyRoute = () => (
  <RequireAuth>
    <RepresentativeController />
  </RequireAuth>
);

const CompanySsoRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <CompanySsoPage />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const HrSyncRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <HrSyncView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const AccountLedgerRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <AccountLedgerView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const CompanyEsgScopeRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <CompanyEsgScopeView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const DeclrAttributeDefinitionRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <ReportingStepFrame stepKey="attribute-definitions">
        <DeclrAttributeDefinitionView />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const DeclrAttributeGroupRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <DeclrAttributeGroupView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const DeclrLocalDeclarationRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <DeclrLocalDeclarationView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const DeclrAttributeMappingRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <ReportingStepFrame stepKey="attribute-mapping">
        <DeclrAttributeMappingView />
      </ReportingStepFrame>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const DeclrChartOfAccountsRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <DeclrChartOfAccountsView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const SupplierWorkspaceRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <SupplierWorkspaceView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const RequestRolesRoute = () => (
  <RequireAuth>
    <RequestRolesPlaceholder />
  </RequireAuth>
);

/**
 * ✅ FINAL ROUTE REGISTRY
 */
export const routes = [
  { path: "/", element: PublicPage },
  { path: "/subscribe", element: SubscribePage },

  // AUTH
  { path: "/auth/register", element: RegisterPage },
  { path: "/auth/sign-in", element: SignInPage },
  { path: "/verify-email", element: VerifyEmailPage },
  { path: "/reset-password", element: ResetPasswordPage },

  // POST-LOGIN HANDOFF
  { path: "/auth/handoff", element: SEC_03_00_PostLoginHandoff },
  { path: "/auth/sso/callback", element: SsoCallbackPage },

  // MAIN APP
  { path: "/registered/dashboard", element: RegisteredDashboardRoute },
  { path: "/registered/data-entry", element: DataEntryWorkspaceRoute },
  { path: "/registered/corporate-details", element: CorporateDetailsRoute },
  { path: "/registered/company-dashboard", element: CompanyDashboardRoute },
  { path: "/registered/company-accounts", element: AccountLedgerRoute },
  { path: "/registered/company-hr", element: HrSyncRoute },
  { path: "/registered/company-sso", element: CompanySsoRoute },
  { path: "/registered/company-scope", element: CompanyEsgScopeRoute },
  { path: "/registered/company-chart-of-accounts", element: DeclrChartOfAccountsRoute },
  { path: "/registered/declr-attribute-groups", element: DeclrAttributeGroupRoute },
  { path: "/registered/declr-local-declaration", element: DeclrLocalDeclarationRoute },
  { path: "/registered/represent-company", element: RepresentCompanyRoute },
  { path: "/registered/suppliers", element: SupplierWorkspaceRoute },
  { path: "/registered/request-roles", element: RequestRolesRoute },

  // ADMIN
  { path: "/registered/site-admin", element: SiteAdminDashboardRoute },

  // ESG TEMPLATE MANAGEMENT
  { path: "/registered/site-admin/report-management", element: ReportManagementRoute },
  { path: "/registered/site-admin/report-management/register-company", element: RegisterCompanyReportingRoute },
  { path: "/registered/site-admin/report-management/conduct", element: ConductReportingRoute },
  // { path: "/registered/site-admin/input-objects", element: InputObjectsRoute },
  { path: "/registered/site-admin/report-component-library", element: ReportComponentLibraryRoute },
  { path: "/registered/site-admin/knowledge-base", element: KnowledgeBaseRoute },
  { path: "/registered/site-admin/knowledge-base-legacy", element: KnowledgeBaseLegacyRoute },
  { path: "/registered/site-admin/meter-catalogue", element: MeterCatalogueRoute },
  // { path: "/registered/site-admin/workflow/template-builder/:templateId?", element: TemplateBuilderRoute },
  { path: "/registered/site-admin/xbrl-processor", element: XbrlProcessorRoute },

  // 🚀 FIXED PATH: ESG REPORT DEFINITION
  { path: "/registered/site-admin/report-definition", element: ReportDefinitionOrchestrationRoute },
  
  { path: "/registered/site-admin/security-portal", element: SEC_03_ManagementPortalRoute },
  { path: "/registered/site-admin/user-roles", element: UserRoleAssignmentRoute },
  { path: "/registered/site-admin/taxonomy-workflow", element: TaxonomyWorkflowRoute },
  { path: "/registered/site-admin/taxonomy", element: TaxonomyIngestRoute },
  { path: "/registered/site-admin/xls-to-schema", element: WorkflowXls2SchemaRoute },
  { path: "/registered/site-admin/workflow_hub", element: WorkflowHubRoute },
  { path: "/registered/site-admin/declr-attribute-definitions", element: DeclrAttributeDefinitionRoute },
  { path: "/registered/site-admin/declr-attribute-mapping", element: DeclrAttributeMappingRoute },

  // MONITORING
  { path: "/registered/upload/monitor", element: SmartUploadMonitorRoute },
  { path: "/registered/s3-inventory", element: S3InventoryMonitorRoute },
];